import React, { forwardRef } from 'react';
import { bool, element, oneOfType, string } from 'prop-types';
import classnames from 'classnames';
import * as css from './radio.css';

/**
 * Displays a Radio Button control.
 *
 * All extra `props` (not accounted for in `propTypes`) get spread on the `<input/>` element, including `ref`.
 */
const Radio = forwardRef((props, ref) => {
    const {
        automationId,
        id,
        className,
        defaultChecked,
        disabled,
        error,
        label,
        name,
        value,
        ...attrs
    } = props;

    const wrapperClasses = classnames(
        className,
        ['pl-4', css.radio, 'relative', 'select-none', 'inline-block', 'font-open-sans-regular'],
        {
            [css.error]: error,
        }
    );

    return (
        <div className={wrapperClasses} data-automation-id={automationId}>
            <input
                id={id}
                ref={ref}
                name={name}
                type="radio"
                value={value}
                disabled={disabled}
                defaultChecked={defaultChecked}
                className="absolute pointer-events-none"
                {...attrs}
            />
            <label htmlFor={id} className="cursor-pointer text-medium text-gray-600">
                {label}
            </label>
        </div>
    );
});

Radio.propTypes = {
    /** Value for `id` attribute on the `<input/>` and `for` attribute on the `<label>`  */
    id: string.isRequired,

    /** Text or `element` for the `<label>`. Use an `element` when custom styling/markup is required. */
    label: oneOfType([string, element]).isRequired,

    /** Value for `name` attribute on the `<input/>`. */
    name: string.isRequired,

    /** Value for `value` attribute on the `<input/>`. */
    value: string.isRequired,

    /** Apply a `data-automation-id` attribute to the `<input/>`. */
    automationId: string,

    /** Add classes to the component wrapper. */
    className: string,

    /** Set the default `checked` state of the `<input/>`. */
    defaultChecked: bool,

    /** Set the `disabled` state of the `<input/>`. */
    disabled: bool,

    /** Show the `<input/>` as invalid. */
    error: bool,
};

Radio.defaultProps = {
    automationId: null,
    className: null,
    defaultChecked: false,
    disabled: false,
    error: false,
};

export default Radio;
